import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardPlus, Image as ImageIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { avCreate, avUploadAttachment } from "@/lib/api";

const Field = ({ label, required, children, className = "" }) => (
  <div className={className}>
    <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
      {label}{required && <span className="text-rose-600">*</span>}
    </label>
    {children}
  </div>
);

const Section = ({ title, subtitle, children }) => (
  <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
    <div className="px-4 py-2.5 border-b border-[#e2e1eb]">
      <div className="text-[13px] font-semibold text-[#1a1b22]">{title}</div>
      {subtitle && <div className="text-[11px] text-[#747684] mt-0.5">{subtitle}</div>}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const inputCls =
  "w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-yamaha-400";

const NewAv = () => {
  const { currentUser, currentLocation, locations } = useApp();
  const navigate = useNavigate();

  const allowed = useMemo(
    () => locations.filter(
      (l) => currentUser?.role === "admin" || currentUser?.location_codes?.includes(l.code)
    ),
    [locations, currentUser]
  );
  const defaultLoc =
    currentLocation && currentLocation !== "ALL" && !currentLocation.includes(",")
      ? currentLocation
      : allowed[0]?.code || "";

  const todayISO = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    location_code: defaultLoc,
    // Customer
    customer_name: "",
    mobile_number: "",
    alternate_number: "",
    address: "",
    town: "",
    // Vehicle
    registration_number: "",
    vin_no: "",
    model_name: "",
    job_card_no: "",
    km_reading: "",
    // Accident
    claim_type: "insured",
    insurance_company: "",
    policy_end_date: "",
    complaint: "",
    receiving_date: todayISO, // NEW: mandatory, defaults to today
    likely_delivery_date: "",
  });
  const [vehiclePhotos, setVehiclePhotos] = useState([]); // [{file, preview}]
  const [damagePhotos, setDamagePhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onCreate = async (asDraft = false) => {
    // Light client-side validation — backend re-validates everything.
    if (!form.location_code) return toast.error("Location is required");
    if (!asDraft) {
      const req = [
        ["customer_name", "Customer Name"],
        ["mobile_number", "Mobile Number"],
        ["registration_number", "Registration Number"],
        ["model_name", "Model"],
        ["receiving_date", "Received On"],
      ];
      for (const [k, label] of req) {
        if (!(form[k] || "").trim()) return toast.error(`${label} is required`);
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        save_as_draft: asDraft,
        km_reading: form.km_reading ? Number(form.km_reading) : null,
        actor: currentUser.name,
        actor_role: currentUser.role,
      };
      const av = await avCreate(payload);

      // Upload photos sequentially (small per-file count, simple to reason).
      const uploads = [
        ...vehiclePhotos.map((p) => ["vehicle_photo", p.file]),
        ...damagePhotos.map((p) => ["damage_photo", p.file]),
      ];
      for (const [kind, file] of uploads) {
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("file", file);
        fd.append("actor", currentUser.name);
        fd.append("actor_role", currentUser.role);
        try {
          await avUploadAttachment(av.id, fd);
        } catch (e) {
          toast.error(`Photo upload failed: ${file.name}`);
        }
      }
      toast.success(`${asDraft ? "Draft saved" : "AV created"} · ${av.av_number}`);
      navigate(`/av/${av.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto space-y-4" data-testid="av-new-page">
      <header className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-yamaha-50 flex items-center justify-center">
          <ClipboardPlus className="w-6 h-6 text-yamaha-700" />
        </div>
        <div>
          <h1 className="text-[24px] font-semibold text-yamaha-800 leading-none tracking-tight">New AV Entry</h1>
          <p className="text-[12.5px] text-[#747684] mt-1">
            Record an accidental vehicle on intake. Photos help downstream surveyor verification.
          </p>
        </div>
      </header>

      <Section title="Location">
        <Field label="Location" required className="md:w-1/2">
          <select
            data-testid="av-location"
            value={form.location_code}
            onChange={(e) => set("location_code", e.target.value)}
            className={inputCls}
          >
            <option value="">— Select location —</option>
            {allowed.map((l) => (
              <option key={l.code} value={l.code}>{l.name} ({l.code})</option>
            ))}
          </select>
        </Field>
      </Section>

      <Section title="Customer Details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Customer Name" required>
            <input data-testid="av-customer-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Mobile Number (WhatsApp preferred)" required>
            <input data-testid="av-mobile" value={form.mobile_number} onChange={(e) => set("mobile_number", e.target.value.replace(/\D/g, "").slice(0, 12))} className={inputCls} />
          </Field>
          <Field label="Alternate Number">
            <input data-testid="av-alt-mobile" value={form.alternate_number} onChange={(e) => set("alternate_number", e.target.value.replace(/\D/g, "").slice(0, 12))} className={inputCls} />
          </Field>
          <Field label="Town">
            <input data-testid="av-town" value={form.town} onChange={(e) => set("town", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Address (as per Aadhaar/Govt ID)" className="md:col-span-2">
            <textarea data-testid="av-address" value={form.address} onChange={(e) => set("address", e.target.value)} rows={2} className={inputCls} />
          </Field>
        </div>
      </Section>

      <Section title="Vehicle Details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Registration Number" required>
            <input data-testid="av-reg-no" value={form.registration_number} onChange={(e) => set("registration_number", e.target.value.toUpperCase())} className={`${inputCls} font-mono uppercase`} />
          </Field>
          <Field label="VIN / Chassis Number">
            <input data-testid="av-vin" value={form.vin_no} onChange={(e) => set("vin_no", e.target.value.toUpperCase())} className={`${inputCls} font-mono uppercase`} />
          </Field>
          <Field label="Model" required>
            <input data-testid="av-model" value={form.model_name} onChange={(e) => set("model_name", e.target.value)} placeholder="e.g. Yamaha R15 V4" className={inputCls} />
          </Field>
          <Field label="Job Card Number">
            <input data-testid="av-jc" value={form.job_card_no} onChange={(e) => set("job_card_no", e.target.value)} className={inputCls} />
          </Field>
          <Field label="KM Reading">
            <input data-testid="av-km" type="number" min="0" value={form.km_reading} onChange={(e) => set("km_reading", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Received On" required>
            <input data-testid="av-receiving-date" type="date" value={form.receiving_date}
              onChange={(e) => set("receiving_date", e.target.value)}
              className={inputCls} />
          </Field>
          <Field label="Likely Delivery Date">
            <input data-testid="av-likely-delivery" type="date" value={form.likely_delivery_date} onChange={(e) => set("likely_delivery_date", e.target.value)} className={inputCls} />
          </Field>
        </div>
      </Section>

      <Section title="Accident / Claim Details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Claim Type">
            <div className="flex gap-2">
              {["insured", "non_insured"].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => set("claim_type", v)}
                  data-testid={`av-claim-${v}`}
                  className={`flex-1 px-3 py-2 rounded-md border text-[12.5px] font-semibold ${
                    form.claim_type === v
                      ? "border-yamaha-500 bg-yamaha-50 text-yamaha-700"
                      : "border-[#e2e1eb] text-[#444653] hover:bg-[#f4f3fc]"
                  }`}
                >
                  {v === "insured" ? "Insured" : "Non Insured"}
                </button>
              ))}
            </div>
          </Field>
          {form.claim_type === "insured" && (
            <>
              <Field label="Insurance Company">
                <input data-testid="av-insurance" value={form.insurance_company} onChange={(e) => set("insurance_company", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Policy End Date">
                <input data-testid="av-policy-end" type="date" value={form.policy_end_date} onChange={(e) => set("policy_end_date", e.target.value)} className={inputCls} />
              </Field>
            </>
          )}
          <Field label="Complaint / Damage Description" className="md:col-span-2">
            <textarea data-testid="av-complaint" value={form.complaint} onChange={(e) => set("complaint", e.target.value)} rows={3} className={inputCls} />
          </Field>
        </div>
      </Section>

      <Section title="Photos" subtitle="Vehicle photos (overall) + damage photos (close-ups). JPG / PNG / WEBP, ≤10 MB each.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PhotoPicker label="Vehicle Photos" photos={vehiclePhotos} setPhotos={setVehiclePhotos} testid="av-vehicle-photos" />
          <PhotoPicker label="Damage Photos" photos={damagePhotos} setPhotos={setDamagePhotos} testid="av-damage-photos" />
        </div>
      </Section>

      <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-[#faf8ff] py-3 -mx-4 px-4 border-t border-[#e2e1eb]">
        <Button variant="outline" onClick={() => navigate("/av/dashboard")} data-testid="av-cancel">
          Cancel
        </Button>
        <Button variant="outline" disabled={submitting} onClick={() => onCreate(true)} data-testid="av-save-draft">
          Save Draft
        </Button>
        <Button disabled={submitting} onClick={() => onCreate(false)} data-testid="av-create" className="bg-yamaha-700 hover:bg-yamaha-800">
          {submitting ? "Saving…" : "Create AV"}
        </Button>
      </div>
    </div>
  );
};

const PhotoPicker = ({ label, photos, setPhotos, testid }) => {
  const inputRef = useRef(null);
  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    const next = files.map((file) => ({ file, preview: URL.createObjectURL(file) }));
    setPhotos((p) => [...p, ...next]);
  };
  const removeAt = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));
  return (
    <div className="border border-dashed border-[#cfcdd8] rounded-lg p-3" data-testid={testid}>
      <div className="text-[11.5px] font-semibold text-[#444653] mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {photos.map((p, i) => (
          <div key={i} className="relative w-20 h-20 rounded border border-[#e2e1eb] overflow-hidden bg-[#faf8ff]">
            <img src={p.preview} alt="" className="object-cover w-full h-full" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="absolute top-0.5 right-0.5 bg-white/90 hover:bg-rose-50 text-rose-600 rounded p-0.5"
              title="Remove"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-20 h-20 flex flex-col items-center justify-center rounded border border-dashed border-[#cfcdd8] text-[10.5px] text-[#747684] hover:border-yamaha-400 hover:bg-yamaha-50/30"
          data-testid={`${testid}-add`}
        >
          <ImageIcon className="w-5 h-5 mb-0.5" />
          Add
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
        />
      </div>
    </div>
  );
};

export default NewAv;
